import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiHelper } from '../services/api';

// Initial state
const initialState = {
  language: 'en',
  theme: 'light',
  isLoading: false,
  notifications: [],
  user: null,
  restaurantInfo: {
    name: 'Pizza Inn',
    phone: '*9777',
    email: 'info@pizzainn.az',
    address: 'Fountain Square 8, Baku',
    mapsUrl: 'https://maps.google.com/?q=Pizza+Inn+Baku+Fountains+Square+8',
    workingHours: {
      daily: '08:00 – 03:00'
    },
    delivery: {
      wolt: 'https://wolt.com/en/aze/baku/restaurant/pizza-inn',
      boltFood: 'https://food.bolt.eu/en-US/335-baku/p/21023-pizza-inn'
    },
    socialMedia: {
      facebook: 'https://www.facebook.com/PizzaInnBaku/',
      instagram: 'https://www.instagram.com/pizzainnbaku/',
      tripadvisor: 'https://www.tripadvisor.com/Restaurant_Review-g293934-d1061020-Reviews-Pizza_Inn-Baku_Absheron_Region.html'
    }
  },
  settings: {
    currency: 'AZN',
    serviceFeeRate: 0.10,
    deliveryFee: 2.00,
    freeDeliveryThreshold: 25.00
  }
};

// Action types
const actionTypes = {
  SET_LANGUAGE: 'SET_LANGUAGE',
  SET_THEME: 'SET_THEME',
  SET_LOADING: 'SET_LOADING',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  SET_USER: 'SET_USER',
  UPDATE_RESTAURANT_INFO: 'UPDATE_RESTAURANT_INFO',
  UPDATE_SETTINGS: 'UPDATE_SETTINGS'
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LANGUAGE:
      return {
        ...state,
        language: action.payload
      };

    case actionTypes.SET_THEME:
      return {
        ...state,
        theme: action.payload
      };

    case actionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case actionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [...state.notifications, {
          id: Date.now(),
          ...action.payload
        }]
      };

    case actionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(
          notification => notification.id !== action.payload
        )
      };

    case actionTypes.SET_USER:
      return {
        ...state,
        user: action.payload
      };

    case actionTypes.UPDATE_RESTAURANT_INFO:
      return {
        ...state,
        restaurantInfo: {
          ...state.restaurantInfo,
          ...action.payload
        }
      };

    case actionTypes.UPDATE_SETTINGS:
      return {
        ...state,
        settings: {
          ...state.settings,
          ...action.payload
        }
      };

    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Custom hook to use app context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// App Provider component
export const AppProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load saved preferences from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('pizzainn_language');
    const savedTheme = localStorage.getItem('pizzainn_theme');

    if (savedLanguage && savedLanguage !== state.language) {
      setLanguage(savedLanguage);
    }

    if (savedTheme && savedTheme !== state.theme) {
      setTheme(savedTheme);
    }
  }, []);

  // The restaurant's own details come from the database, where the admin panel
  // edits them. Everything below is only a first-paint placeholder: without
  // this the footer and the contact page showed the hardcoded values in this
  // file instead, so changing the phone number in admin changed nothing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiHelper.get('/contact/info/details');
        const d = res?.data;
        if (cancelled || !d) return;
        const hours = d.businessHours?.monday || state.restaurantInfo.workingHours.daily;
        dispatch({
          type: actionTypes.UPDATE_RESTAURANT_INFO,
          payload: {
          ...(d.name ? { name: d.name } : {}),
          ...(d.phone ? { phone: d.phone } : {}),
          ...(d.email ? { email: d.email } : {}),
          ...(d.address ? { address: d.address } : {}),
          workingHours: { daily: hours },
          ...(d.socialMedia ? {
            socialMedia: {
              ...state.restaurantInfo.socialMedia,
              ...(d.socialMedia.instagram ? { instagram: d.socialMedia.instagram } : {}),
              ...(d.socialMedia.facebook ? { facebook: d.socialMedia.facebook } : {}),
            },
          } : {}),
          },
        });
      } catch (err) {
        // Offline or the API is down: the placeholders above still render.
      }
    })();
    return () => { cancelled = true; };
    // Intentionally once: the settings change in the database, not per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save language to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('pizzainn_language', state.language);
    i18n.changeLanguage(state.language);
  }, [state.language, i18n]);

  // Save theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('pizzainn_theme', state.theme);
    document.documentElement.setAttribute('data-theme', state.theme);
  }, [state.theme]);

  // Action creators
  const setLanguage = (language) => {
    dispatch({ type: actionTypes.SET_LANGUAGE, payload: language });
  };

  const setTheme = (theme) => {
    dispatch({ type: actionTypes.SET_THEME, payload: theme });
  };

  const setLoading = (isLoading) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: isLoading });
  };

  const addNotification = (notification) => {
    dispatch({ type: actionTypes.ADD_NOTIFICATION, payload: notification });
    
    // Auto-remove notification after delay
    if (notification.autoRemove !== false) {
      setTimeout(() => {
        removeNotification(Date.now());
      }, notification.duration || 5000);
    }
  };

  const removeNotification = (id) => {
    dispatch({ type: actionTypes.REMOVE_NOTIFICATION, payload: id });
  };

  const setUser = (user) => {
    dispatch({ type: actionTypes.SET_USER, payload: user });
  };

  const updateRestaurantInfo = (info) => {
    dispatch({ type: actionTypes.UPDATE_RESTAURANT_INFO, payload: info });
  };

  const updateSettings = (settings) => {
    dispatch({ type: actionTypes.UPDATE_SETTINGS, payload: settings });
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('az-AZ', {
      style: 'currency',
      currency: state.settings.currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Calculate total with service fee
  const calculateTotal = (subtotal) => {
    const serviceFee = subtotal * state.settings.serviceFeeRate;
    return subtotal + serviceFee;
  };

  // Calculate delivery fee
  const calculateDeliveryFee = (subtotal) => {
    return subtotal >= state.settings.freeDeliveryThreshold ? 0 : state.settings.deliveryFee;
  };

  const value = {
    // State
    ...state,
    
    // Actions
    setLanguage,
    setTheme,
    setLoading,
    addNotification,
    removeNotification,
    setUser,
    updateRestaurantInfo,
    updateSettings,
    
    // Helpers
    formatCurrency,
    calculateTotal,
    calculateDeliveryFee
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
